import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { FormField } from '@/shared/components/custom/form-field/FormField';
import { PillRadioGroup } from '@/shared/components/custom/pill-radio-group';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { useCreateTimeOffRequest } from '../../hooks/useCreateTimeOffRequest';
import {
  createRequestSchema,
  type CreateRequestFormInput,
  type CreateRequestFormValues,
} from '../../schemas';
import { DateRangeWithDailyTimeField } from './DateRangeWithDailyTimeField';
import { RequestStepper } from './RequestStepper';
import type { TimeOffRequestDto } from '../../types';

interface RequestFormProps {
  onCreated?: (request: TimeOffRequestDto) => void;
  onCancel?: () => void;
}

export function RequestForm({ onCreated, onCancel }: RequestFormProps) {
  const { t } = useTranslation('timeOff');
  const { data: types } = useTimeOffTypes();
  const createRequest = useCreateTimeOffRequest();
  const [createdRequest, setCreatedRequest] = useState<TimeOffRequestDto | null>(null);

  const { control, handleSubmit, reset } = useForm<
    CreateRequestFormInput,
    unknown,
    CreateRequestFormValues
  >({
    resolver: zodResolver(createRequestSchema(t)),
    defaultValues: { typeId: '', days: [], reason: '' },
  });

  function onSubmit(values: CreateRequestFormValues) {
    createRequest.mutate(
      {
        typeId: values.typeId,
        days: values.days.map((day) => ({
          date: day.date,
          startTime: day.isFullDay ? undefined : day.startTime,
          endTime: day.isFullDay ? undefined : day.endTime,
        })),
        reason: values.reason || undefined,
      },
      {
        onSuccess: (request) => {
          setCreatedRequest(request);
          onCreated?.(request);
        },
      },
    );
  }

  if (createdRequest) {
    return (
      <div className="space-y-4" data-testid="time-off-request-created">
        <p className="text-sm font-medium text-foreground">{t('requests.stepper.pending')}</p>
        <RequestStepper steps={createdRequest.steps ?? []} />
        <Button
          variant="outline"
          onClick={() => {
            setCreatedRequest(null);
            reset();
          }}
        >
          {t('requests.form.createAnother')}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      data-testid="time-off-request-form"
    >
      <FormField
        control={control}
        name="typeId"
        label={t('requests.form.type')}
        render={({ field, invalid, disabled }) => (
          <PillRadioGroup
            id="typeId"
            value={field.value as string}
            onValueChange={field.onChange}
            options={(types ?? []).map((type) => ({
              value: type.id ?? '',
              label: type.name ?? '',
            }))}
            disabled={disabled}
            aria-invalid={invalid}
            data-testid="time-off-request-form-type"
          />
        )}
      />

      <Controller
        name="days"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <FieldLabel>{t('requests.form.dateRange')}</FieldLabel>
            <DateRangeWithDailyTimeField value={field.value ?? []} onChange={field.onChange} />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />

      <FormFieldTextarea
        control={control}
        name="reason"
        label={t('requests.form.reason')}
        data-testid="time-off-request-form-reason"
      />

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('requests.form.cancel')}
          </Button>
        )}
        <Button type="submit" disabled={createRequest.isPending}>
          {t('requests.form.submit')}
        </Button>
      </div>
    </form>
  );
}
